import React, { useCallback, useEffect, useMemo, useState } from "react";
import DateBox from "devextreme-react/date-box";
import { Button } from "devextreme-react/button";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./FundingDashboard.css";
import { baseUrl } from "../hooks/useFetch";
import { toDate, fmtISO } from "../utils/dateUtils";
import { fmtNum } from "../utils/formatUtils";
import {
  FundingRow,
  StressRow,
  GridFundingSensitivity,
  GridStressScenarios,
  pivotFundingIfNeeded,
} from "../components/common/fundingGrids";

interface ScenarioDescription {
  scenario: string;
  description: string;
}

/* ── main component ───────────────────────────────────────────── */
const FundingDashboard: React.FC = () => {
  const getLastDatePrevYear = (): Date => {
    const y = new Date().getFullYear() - 1;
    return new Date(`${y}-12-31`);
  };

  const [cobDate,      setCobDate]      = useState<Date | null>(null);
  const [compareDate,  setCompareDate]  = useState<Date | null>(getLastDatePrevYear());
  const [loading,      setLoading]      = useState<boolean>(true);
  const [leftFunding,  setLeftFunding]  = useState<FundingRow[]>([]);
  const [leftStress,   setLeftStress]   = useState<StressRow[]>([]);
  const [rightFunding, setRightFunding] = useState<FundingRow[]>([]);
  const [rightStress,  setRightStress]  = useState<StressRow[]>([]);

  const scenarioDescriptions = useMemo<ScenarioDescription[]>(() => [
    { scenario: "Gas Halt Dec21",    description: "Price action expected from a halt in gas supply" },
    { scenario: "War Over",          description: "Price action expected from announcement of the end of the war in Ukraine" },
    { scenario: "Europe Gas Flow",   description: "Price action expected from re-introduction of Russian gas flows into Europe" },
    { scenario: "Feb-Mar22",         description: "Repeats the price action that took place between 8 Feb 2022 and 7 Mar 2022" },
    { scenario: "Macro Stress",      description: "Escalation of both Russia vs Ukraine & the West, and Israel vs Arab States" },
    { scenario: "Covid19",           description: "Repeats the price action that took place in March 2020" },
    { scenario: "Ext Weather",       description: "Record-breaking heatwave between July 1st and August 19th, 2022" },
    { scenario: "Gas Halt Dec21 1m", description: "Same as Gas Halt but the impact as the portfolio evolves in 1 month" },
  ], []);

  /* fetch latest CoB */
  useEffect(() => {
    if (cobDate !== null && compareDate !== null) return;
    const ac = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const r    = await fetch(`${baseUrl}/funding_dashboard_latest_cob`, { signal: ac.signal });
        const data = await r.json();
        if (cobDate === null) setCobDate(toDate(data?.cob_date));
      } catch (e: any) { if (e?.name !== "AbortError") console.error(e); }
      finally     { setLoading(false); }
    })();
    return () => ac.abort();
  }, [cobDate, compareDate]);

  /* fetch dashboard data */
  useEffect(() => {
    if (!cobDate || !compareDate) return;
    const ac = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const url  = `${baseUrl}/funding_dashboard?cob_date=${encodeURIComponent(fmtISO(cobDate) ?? "")}&compare_date=${encodeURIComponent(fmtISO(compareDate) ?? "")}`;
        const r    = await fetch(url, { signal: ac.signal });
        const data = await r.json();
        setLeftFunding (data?.left?.funding_sensitivity_by_asset_class  ?? []);
        setLeftStress  (data?.left?.stress_test_scenarios               ?? []);
        setRightFunding(data?.right?.funding_sensitivity_by_asset_class ?? []);
        setRightStress (data?.right?.stress_test_scenarios              ?? []);
      } catch (e: any) { if (e?.name !== "AbortError") console.error("Failed to load funding dashboard", e); }
      finally     { setLoading(false); }
    })();
    return () => ac.abort();
  }, [cobDate, compareDate]);

  /* ── export helpers ───────────────────────────────────────── */
  const fundingCols   = ["Asset Class", "+10% 100% Strat", "+10% 30% Strat", "-10% 100% Strat", "-10% 30% Strat"];
  const stressCols    = ["Scenario", "Total Margin Call", "New Margin Call", "Pending Flows", "Exchange VM"];
  const scenarioCols  = ["Scenario", "Description"];

  function fundingToRows(rows: FundingRow[]): string[][] {
    return rows.map((r) => [
      r.asset_class,
      fmtNum(r.plus10_funding_100_strategic),
      fmtNum(r.plus10_funding_30_strategic),
      fmtNum(r.minus10_funding_100_strategic),
      fmtNum(r.minus10_funding_30_strategic),
    ]);
  }

  function stressToRows(rows: StressRow[]): string[][] {
    return rows.map((r) => [
      r.scenario,
      fmtNum(r.total_margin_call),
      fmtNum(r.new_margin_call),
      fmtNum(r.pending_flows),
      fmtNum(r.exchange_vm),
    ]);
  }

  const exportExcel = async (): Promise<void> => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Funding Dashboard");

    const headerFill: any = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
    const headerFont: any = { bold: true, color: { argb: "FFFFFFFF" } };
    const NUM_COLS = 5;      // 1 label + 4 data per table
    const GAP_COL = 1;       // empty column between left and right tables
    const RIGHT_OFFSET = NUM_COLS + GAP_COL; // right table starts at column 7

    // Set column widths: left table (1-5), gap (6), right table (7-11)
    for (const offset of [0, RIGHT_OFFSET]) {
      ws.getColumn(offset + 1).width = 30;
      for (let c = 2; c <= NUM_COLS; c++) ws.getColumn(offset + c).width = 18;
    }
    ws.getColumn(NUM_COLS + 1).width = 3; // narrow gap column

    const writeTable = (title: string, cols: string[], body: string[][], startRow: number, colOffset: number): number => {
      const base = colOffset + 1;
      ws.getRow(startRow).getCell(base).value = title;
      ws.getRow(startRow).getCell(base).font = { bold: true, size: 11 };

      const hdrRow = startRow + 1;
      for (let c = 0; c < cols.length; c++) {
        const cell = ws.getRow(hdrRow).getCell(base + c);
        cell.value = cols[c];
        cell.fill = headerFill;
        cell.font = headerFont;
        cell.alignment = { vertical: "middle", horizontal: c === 0 ? "left" : "right" };
      }

      let r = hdrRow + 1;
      for (const row of body) {
        for (let c = 0; c < row.length; c++) {
          const cell = ws.getRow(r).getCell(base + c);
          cell.value = row[c];
          if (c > 0) {
            cell.alignment = { horizontal: "right" };
            const txt = row[c] || "";
            if (txt.includes("(") && txt.includes(")")) {
              cell.font = { color: { argb: "FFFF0000" } };
            }
          }
        }
        r++;
      }
      return r + 1;
    };

    let row = 1;
    ws.getRow(row).getCell(1).value = "Funding Dashboard";
    ws.getRow(row).getCell(1).font = { bold: true, size: 14 };
    row += 1;
    ws.getRow(row).getCell(1).value = `CoB: ${fmtISO(cobDate) || ""}`;
    ws.getRow(row).getCell(1 + RIGHT_OFFSET).value = `Comparison: ${fmtISO(compareDate) || ""}`;
    row += 2;

    // Row 1: Funding Sensitivity (left) + Delta (right) — side by side
    const fundingStartRow = row;
    const leftEnd1 = writeTable("Funding Sensitivity by Asset Class", fundingCols, fundingToRows(leftFunding), fundingStartRow, 0);
    const rightEnd1 = writeTable("Funding Sensitivity (Delta CoB - Comparison)", fundingCols, fundingToRows(rightFunding), fundingStartRow, RIGHT_OFFSET);
    row = Math.max(leftEnd1, rightEnd1);

    // Row 2: Stress Test (left) + Delta (right) — side by side
    const stressStartRow = row;
    const leftEnd2 = writeTable("Stress Test Scenarios", stressCols, stressToRows(leftStress), stressStartRow, 0);
    const rightEnd2 = writeTable("Stress Test Scenarios (Delta CoB - Comparison)", stressCols, stressToRows(rightStress), stressStartRow, RIGHT_OFFSET);
    row = Math.max(leftEnd2, rightEnd2);

    // Row 3: Scenario Descriptions — full width
    const scenarioBody = scenarioDescriptions.map((s) => [s.scenario, s.description]);
    writeTable("Scenario Description", scenarioCols, scenarioBody, row, 0);

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Funding_Dashboard_${fmtISO(cobDate) || "export"}.xlsx`);
  };

  const exportPdf = (): void => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 30;
    const gap = 16;
    const halfWidth = (pageWidth - margin * 2 - gap) / 2;
    const leftX = margin;
    const rightX = margin + halfWidth + gap;

    doc.setFontSize(14);
    doc.text("Funding Dashboard", leftX, 30);
    doc.setFontSize(10);
    doc.text(`CoB: ${fmtISO(cobDate) || ""}    Comparison: ${fmtISO(compareDate) || ""}`, leftX, 46);

    const colWidths = (tableWidth: number): number[] => {
      const labelW = tableWidth * 0.3;
      const dataW = (tableWidth - labelW) / 4;
      return [labelW, dataW, dataW, dataW, dataW];
    };

    const addTable = (title: string, cols: string[], body: string[][], startY: number, xOffset: number, width: number): number => {
      doc.setFontSize(9);
      doc.text(title, xOffset, startY);
      const cw = colWidths(width);

      (doc as any).autoTable({
        startY: startY + 6,
        head: [cols],
        body,
        tableWidth: width,
        margin: { left: xOffset, right: pageWidth - xOffset - width },
        styles: { fontSize: 7, cellPadding: 2.5, valign: "middle", overflow: "linebreak" },
        headStyles: { fontStyle: "bold", fontSize: 6.5 },
        columnStyles: {
          0: { cellWidth: cw[0], halign: "left" },
          1: { cellWidth: cw[1], halign: "right" },
          2: { cellWidth: cw[2], halign: "right" },
          3: { cellWidth: cw[3], halign: "right" },
          4: { cellWidth: cw[4], halign: "right" },
        },
        didParseCell: (cellData: any) => {
          if (cellData.section === "body") {
            const txt = (cellData.cell.text || []).join("");
            if (txt.includes("(") && txt.includes(")")) {
              cellData.cell.styles.textColor = [255, 0, 0];
            }
          }
        },
      });

      return (doc as any).lastAutoTable.finalY;
    };

    // Row 1: Funding Sensitivity side by side
    const row1Y = 60;
    const leftEnd1 = addTable("Funding Sensitivity by Asset Class", fundingCols, fundingToRows(leftFunding), row1Y, leftX, halfWidth);
    const rightEnd1 = addTable("Funding Sensitivity (Delta CoB - Comparison)", fundingCols, fundingToRows(rightFunding), row1Y, rightX, halfWidth);
    const row2Y = Math.max(leftEnd1, rightEnd1) + 18;

    // Row 2: Stress Test side by side
    const leftEnd2 = addTable("Stress Test Scenarios", stressCols, stressToRows(leftStress), row2Y, leftX, halfWidth);
    const rightEnd2 = addTable("Stress Test Scenarios (Delta CoB - Comparison)", stressCols, stressToRows(rightStress), row2Y, rightX, halfWidth);
    const row3Y = Math.max(leftEnd2, rightEnd2) + 18;

    // Row 3: Scenario Descriptions — full width
    const fullWidth = pageWidth - margin * 2;
    const scenarioBody = scenarioDescriptions.map((s) => [s.scenario, s.description]);
    doc.setFontSize(9);
    doc.text("Scenario Description", leftX, row3Y);
    (doc as any).autoTable({
      startY: row3Y + 6,
      head: [scenarioCols],
      body: scenarioBody,
      tableWidth: fullWidth,
      margin: { left: leftX, right: margin },
      styles: { fontSize: 8, cellPadding: 3, valign: "middle" },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: fullWidth * 0.25, halign: "left" },
        1: { halign: "left" },
      },
    });

    doc.save(`Funding_Dashboard_${fmtISO(cobDate) || "export"}.pdf`);
  };

  return (
    <div>

      {/* Command bar (date pickers + export buttons) */}
      <div className="topBar">
        <div className="dateBoxWrap">
          <div className="dateLabel">CoB</div>
          <DateBox value={cobDate} type="date" displayFormat="dd/MM/yyyy"
            onValueChanged={(e: any) => setCobDate(e.value)} width={220} />
        </div>
        <div className="dateBoxWrap">
          <div className="dateLabel">Comparison</div>
          <DateBox value={compareDate} type="date" displayFormat="dd/MM/yyyy"
            onValueChanged={(e: any) => setCompareDate(e.value)} width={220} />
        </div>
        {loading && <div className="loadingPill">Loading…</div>}
        <div className="exportButtons">
          <Button
            text="Export Excel"
            stylingMode="contained"
            type="default"
            onClick={exportExcel}
            disabled={loading}
            height={30}
            style={{ backgroundColor: "#28a745", color: "white", borderRadius: "4px", border: "none" }}
          />
          <Button
            text="Export PDF"
            stylingMode="contained"
            type="normal"
            onClick={exportPdf}
            disabled={loading}
            height={30}
            style={{ backgroundColor: "#d9534f", color: "white", borderRadius: "4px", border: "none" }}
          />
        </div>
      </div>

      {/* 4. Scrollable content */}
      <div className="fundingContent">
        <div className="panelGrid2x2">
          <div className="panelCell">
            <GridFundingSensitivity title="Funding sensitivity by Asset Class" rows={leftFunding} />
          </div>
          <div className="panelCell">
            <GridFundingSensitivity title="Funding sensitivity by Asset Class (Δ CoB − Comparison)" rows={rightFunding} />
          </div>
          <div className="panelCell">
            <GridStressScenarios title="Stress Test scenarios" rows={leftStress} />
          </div>
          <div className="panelCell">
            <GridStressScenarios title="Stress Test scenarios (Δ CoB − Comparison)" rows={rightStress} />
          </div>
        </div>

        <div className="card scenarioCard">
          <div className="cardTitle">Scenario description</div>
          <DataGrid className="dx-compact-grid" dataSource={scenarioDescriptions}
            showBorders rowAlternationEnabled height={260} width="100%" columnAutoWidth={false}>
            <Paging enabled={false} />
            <Column dataField="scenario"    caption="Scenario" width="25%" minWidth={120} />
            <Column dataField="description" caption="Description" />
          </DataGrid>
        </div>
      </div>
    </div>
  );
};

export default FundingDashboard;
